import type { FunctionComponent, ReactNode, ReactNodeArray } from 'react';
import { Children } from 'react';
import classnames from 'classnames';

type FormGroupProps = {
  /**
   * A set of React form elements (could be a single element).
   */
  children: ReactNode | ReactNodeArray;
  /**
   * Amount of margin bottom for each element (@default 'lg')
   */
  spacing?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Optional legend for the labeling fieldset.
   */
  legend?: ReactNode;
  /**
   * Alignment for the legend vs the form elements. (@default 'above').
   */
  alignLegend?: 'above' | 'left';
  /**
   * Direction for the form elements to be rendered (@default 'column').
   */
  direction?: 'column' | 'row';
  /**
   * Whether to set the "flex-1" class on child elements (@default false).
   */
  itemsFlex1?: boolean;
  /**
   * If provided, this can help a row of elements center-align with the input fields inside them.
   */
  baselineAlignment?: boolean;
};

/**
 * A FormGroup is a semantic way of grouping a column of Form elements, providing consistent vertical/horizontal spacing.
 * It also allows you to add a `<legend>` element to give the group semantic meaning.
 */
const FormGroup: FunctionComponent<FormGroupProps> = ({
  children,
  legend,
  direction = 'column',
  alignLegend = 'above',
  spacing = 'lg',
  itemsFlex1 = false,
  baselineAlignment = false,
}) => (
  <fieldset className={classnames('fossa-form__group', {
    'fossa-form__group--column': alignLegend === 'above',
    'fossa-form__group--row': alignLegend === 'left',
  })}>
    {legend && (
      <span className={`fossa-form__group__legend fossa-form__group__legend--spacing--${spacing}`}>
        <legend> {/* Note: wrap the legend, as it is not a valid flex child. Read more: https://stackoverflow.com/q/35464067/4060044 */}
          {legend}
        </legend>
      </span>
    )}
    <div className={classnames('fossa-form__group__elements', {
      'fossa-form__group__elements--column': direction === 'column',
      'fossa-form__group__elements--row': direction === 'row',
      'fossa-form__group__elements--base-align': baselineAlignment,
    })}>
      {Children.map(children, (formElement) => (
        <div className={classnames(`fossa-form__group__element fossa-form__group__element--${spacing}`, {
          'flex-1': itemsFlex1,
        })}>
          {formElement}
        </div>
      ))}
    </div>
  </fieldset>
);

export default FormGroup;
